export type ForgePluginId = "gmail" | "google-calendar" | "github" | "computer-use";

const FORGE_PLUGIN_IDS: ReadonlySet<string> = new Set([
  "gmail",
  "google-calendar",
  "github",
  "computer-use",
]);

export type PluginConnectionState = "connected" | "error" | "pending" | "unknown";
export type PluginConnectionKind = "cmmdConnector" | "github";

export interface ForgePlugin {
  readonly id: ForgePluginId;
  readonly name: string;
  readonly description: string;
  readonly status: string;
  readonly authReady: boolean | null;
  readonly authReason: string | null;
  readonly connectionKind: PluginConnectionKind;
  readonly capabilities?: { readonly connect: boolean; readonly disconnect: boolean };
  // Account identity to surface once connected (e.g. the GitHub login).
  readonly accountLabel?: string | null;
}

export interface PluginConnectionAttempt {
  readonly pluginId: ForgePluginId;
  readonly status: "opening" | "waiting";
}

export const PLUGIN_FALLBACKS: ReadonlyArray<ForgePlugin> = [
  {
    id: "gmail",
    name: "Gmail",
    description: "Search and read recent email context from Gmail.",
    status: "pending",
    authReady: false,
    authReason: "not_installed",
    connectionKind: "cmmdConnector",
  },
  {
    id: "google-calendar",
    name: "Google Calendar",
    description: "Read upcoming events and search calendar context.",
    status: "pending",
    authReady: false,
    authReason: "not_installed",
    connectionKind: "cmmdConnector",
  },
  {
    id: "github",
    name: "GitHub",
    description: "Create repositories, open pull requests, and manage workflow runs.",
    status: "pending",
    authReady: false,
    authReason: "not_installed",
    connectionKind: "github",
  },
];

export function sortedPlugins(plugins: ReadonlyArray<ForgePlugin>): ReadonlyArray<ForgePlugin> {
  const byId = new Map(plugins.map((plugin) => [plugin.id, plugin]));
  return PLUGIN_FALLBACKS.map((fallback) => byId.get(fallback.id) ?? fallback);
}

export function isForgePluginId(value: string): value is ForgePluginId {
  return FORGE_PLUGIN_IDS.has(value);
}

export function pluginConnectionState(plugin: ForgePlugin): PluginConnectionState {
  if (
    plugin.authReason === "preferred_account_not_active" ||
    plugin.authReason === "preferred_account_not_found" ||
    plugin.authReason === "no_active_connection" ||
    plugin.authReason === "repo_access_unavailable" ||
    plugin.authReason === "cmmd_reauth_required"
  ) {
    return "error";
  }
  if (plugin.authReady === true || plugin.status === "installed") return "connected";
  if (plugin.status === "pending") return "pending";
  return "unknown";
}

export function pluginStateLabel(state: PluginConnectionState): string {
  switch (state) {
    case "connected":
      return "Connected";
    case "error":
      return "Needs reconnect";
    case "pending":
      return "Not connected";
    case "unknown":
      return "Unknown";
  }
}

export function isPluginConnected(
  plugins: ReadonlyArray<ForgePlugin>,
  pluginId: ForgePluginId,
): boolean {
  const plugin = plugins.find((entry) => entry.id === pluginId);
  return plugin ? pluginConnectionState(plugin) === "connected" : false;
}
